import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ListPagination } from '@/components/shared/ListPagination'
import { ContractStatusBadge } from '@/components/shared/StatusBadges'
import { DateRangeFilter } from '@/components/clients/tabs/DateRangeFilter'
import {
  ClientTabSection,
  clientTabFilterOptionClass,
  clientTabFilterTriggerClass,
  clientTabPrimaryBtnClass,
  clientTabSearchInputClass,
  clientTabTableShadow,
  clientTabTdClass,
  clientTabFirstTdClass,
  clientTabThClass,
  clientTabActionsThClass,
  clientTabActionsTdClass,
  clientTabViewDetailsBtnClass,
} from '@/components/clients/tabs/ClientTabSection'
import { useClientContracts } from '@/hooks/useClients'
import { useListParams } from '@/hooks/useListParams'
import { formatCurrency, formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { ContractStatus } from '@/types'

const STATUS_OPTIONS: ContractStatus[] = [
  'draft',
  'active',
  'to_be_renewed',
  'pending_signature',
  'signed',
  'suspended',
  'expired',
  'renewed',
  'declined',
  'terminated',
]
const ALL = 'all'

type Filters = {
  status?: string
  date_from?: string
  date_to?: string
}

const COLUMN_COUNT = 6

// 2.4.8 Client details — contracts tab.
export function ContractsTab({
  clientId,
  canCreateContract = false,
}: {
  clientId: string
  canCreateContract?: boolean
}) {
  const { t } = useTranslation(['clients', 'enums', 'common'])
  const navigate = useNavigate()
  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useListParams<Filters>()
  const { data, isLoading, isError } = useClientContracts(clientId, query)

  const contracts = data?.data ?? []
  const openContract = (contractId: string) =>
    navigate(`/contracts/${contractId}`)

  return (
    <ClientTabSection
      title={t('detail.tabs.contracts')}
      toolbar={
        <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
          <div className='flex min-w-0 flex-wrap items-center gap-4'>
            <DateRangeFilter
              appearance='field'
              triggerClassName={FORM_CONTROL_FOCUS_CLASS}
              from={filters.date_from}
              to={filters.date_to}
              onChange={setFilters}
            />
            <Select
              value={filters.status ?? ALL}
              onValueChange={v =>
                setFilters({ status: v === ALL ? undefined : v })
              }
            >
              <SelectTrigger className={clientTabFilterTriggerClass}>
                <SelectValue placeholder={t('tabFilters.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} className={clientTabFilterOptionClass}>
                  {t('tabFilters.allStatuses')}
                </SelectItem>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem
                    key={s}
                    value={s}
                    className={clientTabFilterOptionClass}
                  >
                    {t(`enums:contractStatus.${s}`, { defaultValue: s })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='flex flex-col gap-4 sm:flex-row sm:items-center'>
            <div className='relative min-w-0 flex-1'>
              <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#3d434e]' />
              <Input
                value={search}
                onChange={e => onSearchChange(e.target.value)}
                placeholder={t('contracts.searchPlaceholder')}
                maxLength={50}
                className={cn(clientTabSearchInputClass, 'max-w-[320px]')}
              />
            </div>
            {canCreateContract && (
              <Button
                className={clientTabPrimaryBtnClass}
                onClick={() => navigate(`/contracts/new?client_id=${clientId}`)}
              >
                <Plus className='size-4' /> {t('contracts.addNew')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div
        className={cn(
          'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white',
          clientTabTableShadow
        )}
      >
        <div className='overflow-x-auto'>
          <table className='w-full caption-bottom text-sm [&_th:nth-child(2)]:text-center [&_td:nth-child(2)]:text-center [&_th:nth-child(3)]:text-center [&_td:nth-child(3)]:text-center'>
            <TableHeader>
              <TableRow className='border-0'>
                <TableHead className={clientTabThClass}>
                  {t('contracts.columns.reference')}
                </TableHead>
                <TableHead className={clientTabThClass}>
                  {t('contracts.columns.status')}
                </TableHead>
                <TableHead className={clientTabThClass}>
                  {t('contracts.columns.amount')}
                </TableHead>
                <TableHead className={clientTabThClass}>
                  {t('contracts.columns.period')}
                </TableHead>
                <TableHead className={clientTabThClass}>
                  {t('contracts.columns.createdDate')}
                </TableHead>
                <TableHead className={clientTabActionsThClass}>
                  {t('list.columns.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i} className='border-0'>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={clientTabTdClass}
                    >
                      <Skeleton className='h-6 w-full' />
                    </TableCell>
                  </TableRow>
                ))}

              {isError && !isLoading && (
                <TableRow className='border-0'>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      clientTabTdClass,
                      'py-10 text-center text-destructive'
                    )}
                  >
                    {t('contracts.error')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !isError && contracts.length === 0 && (
                <TableRow className='border-0'>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      clientTabTdClass,
                      'py-10 text-center text-muted-foreground'
                    )}
                  >
                    {t('contracts.empty')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                !isError &&
                contracts.map(c => (
                  <TableRow
                    key={c.id}
                    className='group relative cursor-pointer border-0'
                    onClick={() => openContract(c.id)}
                  >
                    <TableCell className={clientTabFirstTdClass}>
                      <div className='text-sm font-medium leading-[1.4] text-[#171a20] group-hover:text-[#ff5a1f]'>
                        {c.number ?? c.id.slice(0, 8)}
                      </div>
                    </TableCell>
                    <TableCell className={clientTabTdClass}>
                      <ContractStatusBadge status={c.status} />
                    </TableCell>
                    <TableCell className={clientTabTdClass}>
                      <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                        {formatCurrency(c.amount, 'EUR', 2)}
                      </span>
                    </TableCell>
                    <TableCell className={clientTabTdClass}>
                      <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                        {`${formatDate(c.start_date)} - ${formatDate(c.end_date)}`}
                      </span>
                    </TableCell>
                    <TableCell className={clientTabTdClass}>
                      <span className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                        {formatDate(c.created_at)}
                      </span>
                    </TableCell>
                    <TableCell className={clientTabActionsTdClass}>
                      <button
                        type='button'
                        onClick={e => {
                          e.stopPropagation()
                          openContract(c.id)
                        }}
                        className={clientTabViewDetailsBtnClass}
                      >
                        {t('common:actions.view')}
                        <ArrowRight className='h-4 w-4 shrink-0' />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </table>
        </div>

        <ListPagination
          className='border-t border-[#e7e9ef]'
          metadata={data?.metadata}
          page={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
          showShowingRange={false}
        />
      </div>
    </ClientTabSection>
  )
}
